import type { CSSProperties } from 'react';
import type { ImageBlockProps } from '../types';
import { theme } from '../theme';
import { imageFocusPosition } from '../imageFocus';

/** A positive pixel size, or undefined. Tolerates numeric strings from non-form writers. */
function px(v: unknown): number | undefined {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
}

/**
 * The image's sizing. With no Width/Height it fills the figure as before. A set size is
 * inline style because `w-full` and Tailwind's preflight `img { height: auto }` both
 * override the width/height attributes, which is why the Studio fields used to do nothing.
 * Width never exceeds the figure (max-width: 100%), so a big number can't break phones.
 */
export function imageSizeStyle(width: unknown, height: unknown): { className: string; style?: CSSProperties } {
  const w = px(width);
  const h = px(height);
  if (w === undefined && h === undefined) return { className: 'w-full' };
  const style: CSSProperties = { maxWidth: '100%' };
  style.width = w !== undefined ? `${w}px` : h !== undefined ? 'auto' : '100%';
  style.height = h !== undefined ? `${h}px` : 'auto';
  return { className: 'mx-auto block', style };
}

/** Pure image/figure block. */
export function ImageBlock({ url, alt, caption, width, height, focus }: ImageBlockProps) {
  const size = imageSizeStyle(width, height);
  // The focus point: which part stays in view when a set size crops the photo.
  const position = imageFocusPosition(focus);
  const style = position ? { ...size.style, objectPosition: position } : size.style;
  return (
    <figure className="mx-auto max-w-4xl px-6 py-8">
      <img src={url} alt={alt} width={px(width)} height={px(height)} loading="lazy" decoding="async" className={`${size.className} rounded-lg object-cover`} style={style} />
      {caption ? <figcaption className="mt-2 text-center text-sm" style={{ color: theme.textMuted }}>{caption}</figcaption> : null}
    </figure>
  );
}
